import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { X, ExclamationTriangle, Trash3 } from 'react-bootstrap-icons';
import './DeleteReceipt.scss';
import { toast } from 'react-toastify';
import MiniLoadingSpinner from '../../../../../../../../../components/Spinners/MiniLoadingSpinner';
 
interface DeleteReceiptProps {
  show: boolean;
  onHide: () => void;
  receipts: any[];
  currentReceiptId: number;
  onDelete: () => void;
}

const DeleteReceipt: React.FC<DeleteReceiptProps> = ({
  show,
  onHide,
  receipts,
  currentReceiptId,
  onDelete
}) => {
  const [loading, setLoading] = useState(false);
  
  const isMultipleReceipts = receipts.length > 1;
  const currentReceipt = receipts.find(r => r.receipt_id === currentReceiptId);
  const receiptNumber = currentReceipt?.receipt_number || currentReceiptId;

  const handleDelete = async () => {
    setLoading(true);
    
    try {
      await onDelete();
      toast.success(
        isMultipleReceipts 
          ? 'Todos los recibos de la mesa han sido eliminados' 
          : 'Recibo eliminado exitosamente'
      );
      onHide();
    } catch (error) {
      console.error('Error deleting receipt(s):', error);
      toast.error('Error eliminando recibo(s)');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onHide();
    }
  };

  if (loading) {
    return (
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Body>
          <MiniLoadingSpinner left="50%" />
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal 
      show={show} 
      onHide={handleClose}
      centered
      backdrop={loading ? "static" : true}
      keyboard={!loading}
      className="delete-receipt-modal"
    >
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">
            <Trash3 />
            Eliminar Recibo{isMultipleReceipts ? 's' : ''}
          </Modal.Title>
        </div>
        <Button variant="none" onClick={handleClose} disabled={loading}>
          <X size={24} />
        </Button>
      </Modal.Header>
      
      <Modal.Body>
        <div className="receipt-info-section">
          <div className="receipt-reference">
            <span className="reference-label">Recibo actual:</span>
            <span className="reference-number">#{receiptNumber}</span>
          </div>
        </div>

        {/* Warning banner for deletion confirmation */}
        <div className="warning-banner deletion-warning">
          <ExclamationTriangle className="warning-icon" />
          <div className="warning-content">
            <div className="warning-title">Confirmación de eliminación</div>
            {isMultipleReceipts ? (
              <div className="warning-message">
                Esta mesa tiene <span className="count-highlight">{receipts.length} recibos</span>. 
                Al eliminar, se borrarán <span className="count-highlight">todos los recibos</span> asociados a esta mesa.
              </div>
            ) : (
              <div className="warning-message">
                Se eliminará permanentemente el recibo <span className="receipt-highlight">#{receiptNumber}</span>.
              </div>
            )}
          </div>
        </div>
        
        <div className="action-summary-section">
          <span className="action-summary-label">
            {isMultipleReceipts ? 'Recibos a eliminar:' : 'Acción:'}
          </span>
          <span className="action-summary-value">
            {isMultipleReceipts 
              ? `${receipts.length} recibos de la mesa`
              : 'Eliminar 1 recibo'
            }
          </span>
        </div>
      </Modal.Body>
      
      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button
            variant="none"
            className="form-button-cancel"
            onClick={handleClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="none"
            className="form-button-delete"
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? 'Eliminando...' : `Eliminar ${isMultipleReceipts ? 'Todos' : 'Recibo'}`}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteReceipt;